import { useMutation } from '@tanstack/react-query';
import { apiFetch, setAccessToken } from '@/lib/api';
import { useAuth } from './hooks';
import type {
  LoginInput,
  LoginOutput,
  RegisterInput,
  RegisterOutput,
  LogoutInput,
  LogoutOutput,
} from '@shared/validators/auth.schema';
import type { EmailVerifySendOutput } from '@shared/validators/email-verification.schema';
import type {
  PasswordForgotInput,
  PasswordForgotOutput,
  PasswordResetInput,
  PasswordResetOutput,
} from '@shared/validators/password.schema';
import type {
  LoginMfaRequiredOutput,
  MfaChallengeInput,
} from '@shared/validators/mfa.schema';

/*
 * Auth-mutaatiot (Sessio 5b).
 *
 * TanStack Query -mutaatiot login/register/logout-flow:lle. Refresh-flow on
 * apiFetch:n ja AuthContext:n vastuulla, ei kayta erillista hookia.
 *
 * Vastauksesta jaetetaan refresh_token pois -- se asetetaan cookieksi
 * backendissa setRefreshCookie():lla, ei palauteta JSON-rungossa.
 */

export type LoginSuccessResponse = Omit<LoginOutput, 'refresh_token'>;
export type RegisterResponse = Omit<RegisterOutput, 'refresh_token'>;

/**
 * Sessio 12b: login voi nyt palauttaa joko onnistuneen tokenin tai
 * MFA-vaatimuksen. Backend palauttaa 200 + { mfa_required: true,
 * mfa_challenge_id } jos user.mfa_enabled=1.
 */
export type LoginResponse = LoginSuccessResponse | LoginMfaRequiredOutput;

export function isMfaRequiredResponse(
  response: LoginResponse,
): response is LoginMfaRequiredOutput {
  return 'mfa_required' in response && response.mfa_required === true;
}

export function useLoginMutation() {
  const { setSession } = useAuth();
  return useMutation<LoginResponse, Error, LoginInput>({
    mutationFn: (input) =>
      apiFetch<LoginResponse>('/auth/login', { method: 'POST', body: input }),
    onSuccess: (data) => {
      // MFA-haarautuminen: ei aseteta sessiota viela, kayttaja redirectaan
      // /auth/mfa-challenge -reitille. Tama tehdaan LoginPage:ssa onSuccess-
      // callbackissa, ei taalla.
      if (isMfaRequiredResponse(data)) {
        return;
      }
      setAccessToken(data.access_token);
      setSession(data.user, data.organization);
    },
  });
}

/**
 * Sessio 12b: MFA-challenge mutaatio (login-flow:n step 2).
 * Onnistuessa antaa access-tokenit kuten login:n success-haarautuminen.
 */
export function useMfaChallengeMutation() {
  const { setSession } = useAuth();
  return useMutation<LoginSuccessResponse, Error, MfaChallengeInput>({
    mutationFn: (input) =>
      apiFetch<LoginSuccessResponse>('/auth/mfa-challenge', { method: 'POST', body: input }),
    onSuccess: (data) => {
      setAccessToken(data.access_token);
      setSession(data.user, data.organization);
    },
  });
}

export function useRegisterMutation() {
  const { setSession } = useAuth();
  return useMutation<RegisterResponse, Error, RegisterInput>({
    mutationFn: (input) =>
      apiFetch<RegisterResponse>('/auth/register', { method: 'POST', body: input }),
    onSuccess: (data) => {
      setAccessToken(data.access_token);
      setSession(data.user, data.organization);
    },
  });
}

export function useLogoutMutation() {
  const { clearSession } = useAuth();
  return useMutation<LogoutOutput, Error, LogoutInput | undefined>({
    mutationFn: (input) =>
      apiFetch<LogoutOutput>('/auth/logout', {
        method: 'POST',
        body: input ?? { revoke_all: false },
      }),
    onSuccess: () => {
      clearSession();
    },
    onError: () => {
      // Vaikka logout epaonnistuu (verkko / 500), paastetaan kayttaja ulos
      // paikallisesti. Token tyhjataan ja redirect /login:iin.
      clearSession();
    },
  });
}

/**
 * Pyyda email-vahvistuslinkki (Sessio 6b -> Sessio 7a Turnstile-tokenilla).
 * Backend lahettaa linkin Resend:n kautta (Sessio 7a: oikea integraatio jos
 * RESEND_API_KEY asetettu prod:lla).
 *
 * Dev-ympäristössä vasteessa palautetaan `dev_preview.verification_link` jonka
 * voi avata suoraan testaukseen.
 */
export function useEmailVerifyResendMutation() {
  return useMutation<EmailVerifySendOutput, Error, { turnstile_token: string }>({
    mutationFn: (input) =>
      apiFetch<EmailVerifySendOutput>('/auth/email/verify/send', {
        method: 'POST',
        body: input,
      }),
  });
}

/**
 * Aloita salasanan-resetin pyynto (Sessio 7b). Backend palauttaa AINA
 * `{ sent: true }` riippumatta siita loytyiko email -- emme paljasta olemassaoloa.
 */
export function useForgotPasswordMutation() {
  return useMutation<PasswordForgotOutput, Error, PasswordForgotInput>({
    mutationFn: (input) =>
      apiFetch<PasswordForgotOutput>('/auth/password/forgot', {
        method: 'POST',
        body: input,
      }),
  });
}

/**
 * Suorita salasanan-resetin tokenilla + uudella salasanalla (Sessio 7b).
 * Onnistuessa kaikki kayttajan refresh-tokenit mitatoituvat (force-logout).
 */
export function useResetPasswordMutation() {
  return useMutation<PasswordResetOutput, Error, PasswordResetInput>({
    mutationFn: (input) =>
      apiFetch<PasswordResetOutput>('/auth/password/reset', {
        method: 'POST',
        body: input,
      }),
  });
}
